import React from "react";
import { motion } from "framer-motion";
import { BookOpen, Smartphone, Sparkles, ArrowRight, Check } from "lucide-react";
import { SectionHeader } from "@/components/sections/Services";
import { HOME } from "@/constants/testIds";

export default function Manga() {
  return (
    <section
      id="manga"
      data-testid={HOME.mangaSection}
      className="relative py-32"
    >
      <div className="aurora-orb bg-[#8A2BE2] w-[600px] h-[600px] -left-40 top-1/3 opacity-30" />
      <div className="container-x relative">
        <SectionHeader
          eyebrow="Manga Monetisation"
          title={
            <>
              Monetize Your
              <br />
              <span className="aurora-text">Manga Content </span>
              Effectively
            </>
          }
          subtitle="Specialized advertising solutions designed specifically for manga publishers and content creators to maximize revenue while maintaining reader experience."
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="grid lg:grid-cols-[1.2fr_1fr] gap-8 mb-12"
        >
          <div className="tile p-8 md:p-12">
            <h3 className="font-display text-3xl md:text-4xl font-bold mb-5">
              Manga-Optimized
              <br />
              <span className="cyan-text">Ad Solutions</span>
            </h3>
            <p className="text-white/65 mb-7 max-w-xl">
              Our specialized manga monetization platform understands the unique
              needs of manga publishers. We provide non-intrusive ad formats
              that respect the reading experience while delivering exceptional
              revenue performance.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Chapter-based ad placement optimization",
                "Reader-friendly ad formats",
                "Genre-specific targeting",
                "Mobile-first manga experience",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-white/80">
                  <Check className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            <a
              href="/manga"
              data-testid="home-manga-explore"
              className="btn-primary"
            >
              Explore Manga Solutions
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="glass-strong rounded-3xl p-7 relative">
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm font-medium">Manga Performance</span>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live
              </span>
            </div>
            <div className="space-y-5">
              <PerfRow label="Chapter Views" value="1.2M" />
              <PerfRow label="Reader Retention" value="87%" />
              <PerfRow label="Revenue/Chapter" value="$2.40" highlight />
            </div>
            <div className="mt-6 grid grid-cols-7 gap-1.5 h-20 items-end">
              {[40, 55, 35, 70, 60, 85, 75].map((h, i) => (
                <div
                  key={i}
                  className="rounded-t bg-gradient-to-t from-[#8A2BE2]/40 to-[#00F0FF]"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: BookOpen,
              title: "Reader-First",
              subtitle: "Approach",
              text: "Non-intrusive ad placements that maintain the manga reading flow while maximizing engagement and revenue potential.",
              bullets: [
                "Between-chapter placements",
                "Contextual manga ads",
                "Minimal reading disruption",
              ],
            },
            {
              icon: Smartphone,
              title: "Mobile",
              subtitle: "Optimized",
              text: "Perfect for mobile manga readers with responsive ad formats that work seamlessly across all devices and screen sizes.",
              bullets: [
                "Touch-friendly interfaces",
                "Fast loading times",
                "Vertical reading support",
              ],
            },
            {
              icon: Sparkles,
              title: "Genre",
              subtitle: "Targeting",
              text: "Advanced targeting based on manga genres, reader demographics, and content preferences for higher engagement rates.",
              bullets: [
                "Shonen/Shoujo targeting",
                "Demographic matching",
                "Interest-based ads",
              ],
            },
          ].map((c) => (
            <div key={c.title} className="tile p-7">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8A2BE2]/30 to-[#00F0FF]/20 border border-white/10 grid place-items-center mb-4">
                <c.icon className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h3 className="font-display text-xl font-bold">
                {c.title}
                <span className="block text-sm text-white/50 font-normal">
                  {c.subtitle}
                </span>
              </h3>
              <p className="text-sm text-white/60 my-4">{c.text}</p>
              <ul className="space-y-1.5">
                {c.bullets.map((b) => (
                  <li key={b} className="text-xs text-white/55 flex gap-2">
                    <span className="text-[#00F0FF]">•</span> {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PerfRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-3">
      <span className="text-sm text-white/55">{label}</span>
      <span
        className={`font-mono font-bold ${highlight ? "text-[#00F0FF] text-xl" : "text-white text-lg"}`}
      >
        {value}
      </span>
    </div>
  );
}
